import styled, { css } from 'styled-components';
import { focusVisibleStylesNoWrapper } from 'design-system/src/sharedStyles';

/**
 * Note: Many of these colors are not in our design system (or are used in unexpected
 * ways). We also are using colors with opacity which we know breaks accessibility
 * tooling, but we're accepting this tradeoff for now to maintain rough consistency
 * with the PandaDoc UI that we're replacing.
 *
 * Colors that are linked to system colors below should be called out.
 */

// ------------------------------------------------------------------------------------
// Font size styles
// ------------------------------------------------------------------------------------

/**
 * We style text size relative to the dimensions of the PDF they're rendered on, which is slightly
 * different from the size of the viewport (e.g. different for portrait mode vs. landscape mode screens)
 *
 * This variable is being set in JS in `PDFDisplay` when we pick the scale of the PDF.
 */
export const PDF_UNIT_VAR_NAME = '--pdf-unit';
export const bodyFontSizeStyles = css`
  font-size: calc(var(${PDF_UNIT_VAR_NAME}) * 1.2);
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
`;

// ------------------------------------------------------------------------------------
// Begin text input styles
// ------------------------------------------------------------------------------------

const FILLED_HOVER_BG = '#EAE8E999';
const FILLED_BG = '#FFFFFF99';
const FILLED_BORDER_COLOR = '#D0CBD7';
const FILLED_BORDER_COLOR_HOVER = 'var(--grayscale-900)';
const FILLED_FOCUS_BG = '#E5C1DC';

const EMPTY_REQUIRED_BG = '#ECDBCC99';
const EMPTY_HOVER_BG = '#D9C3B999';

const EMPTY_OPTIONAL_BG = '#FCF3EB99';
const EMPTY_BORDER_COLOR = '#D0CBD7';
const EMPTY_BORDER_COLOR_HOVER = 'var(--grayscale-900)';
const EMPTY_FOCUS_BG = '#E5C1DC';

const READ_ONLY_BG = '#EFEDF1CC'; // This is --grayscale-50 with opacity
const READ_ONLY_HOVER_BG = '#5A4B6E14';
const READ_ONLY_BORDER_COLOR = 'transparent';
const READ_ONLY_BORDER_COLOR_HOVER = 'transparent';
const READ_ONLY_FOCUS_BG = '#D3CDDA99';

// Styled wrapper for text + date inputs
export const StyledTextInput = styled.input<{
  $isUnassigned?: boolean;
  $isEmpty?: boolean;
  $isRequired?: boolean;
  $isReadOnly?: boolean;
  $fieldType?: 'text' | 'date';
}>`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border-style: solid;
  border-color: transparent;
  border-width: 1px;
  border-radius: 2px;
  border-color: ${({ $isEmpty, $isUnassigned }) => {
    if ($isUnassigned) return READ_ONLY_BORDER_COLOR;
    if ($isEmpty) return EMPTY_BORDER_COLOR;
    return FILLED_BORDER_COLOR;
  }};
  opacity: ${({ $isUnassigned }) => ($isUnassigned ? 0.8 : 1)};
  padding: 0 calc(var(${PDF_UNIT_VAR_NAME}) * 0.5);
  ${bodyFontSizeStyles}
  color: ${({ $isUnassigned }) => ($isUnassigned ? 'var(--grayscale-600)' : 'var(--grayscale-900)')};
  line-height: 1;
  vertical-align: top;

  &:read-only {
    cursor: not-allowed;
  }

  background-color: ${({ $isUnassigned, $isEmpty, $isRequired }) => {
    if ($isUnassigned) return READ_ONLY_BG;
    if ($isRequired) {
      if ($isEmpty) return EMPTY_REQUIRED_BG;
      return FILLED_BG;
    } else {
      if ($isEmpty) return EMPTY_OPTIONAL_BG;
      return FILLED_BG;
    }
  }};

  &:hover {
    background-color: ${({ $isEmpty, $isUnassigned }) => {
      if ($isUnassigned) return READ_ONLY_HOVER_BG;
      if ($isEmpty) return EMPTY_HOVER_BG;
      return FILLED_HOVER_BG;
    }};

    border-color: ${({ $isEmpty, $isUnassigned }) => {
      if ($isUnassigned) return READ_ONLY_BORDER_COLOR_HOVER;
      if ($isEmpty) return EMPTY_BORDER_COLOR_HOVER;
      return FILLED_BORDER_COLOR_HOVER;
    }};
  }

  &:focus,
  &:focus-within,
  &:focus-visible {
    /* Remove the original border on focus */

    background-color: ${({ $isUnassigned, $isEmpty }) => {
      if ($isUnassigned) return READ_ONLY_FOCUS_BG;
      if ($isEmpty) return EMPTY_FOCUS_BG;
      return FILLED_FOCUS_BG;
    }};
    ${focusVisibleStylesNoWrapper}
  }
  ${({ $fieldType }) => {
    if ($fieldType === 'date') {
      return `
        &::-webkit-calendar-picker-indicator {
          opacity: 0.6;
          cursor: pointer;
        }

        &:read-only::-webkit-calendar-picker-indicator {
          cursor: not-allowed;
          opacity: 0.3;
        }
      `;
    }
  }}
`;

// ------------------------------------------------------------------------------------
// Begin signature styles
// ------------------------------------------------------------------------------------

export const SignatureContainer = styled.button.attrs({
  type: 'button',
  // @ts-expect-error Adding a custom attribute to allow this to receive focus instead of the actual checkbox
  'data-focusable': 'true',
})<{
  $isUnassigned: boolean;
  $isEmpty: boolean;
  $isRequired: boolean;
}>`
  all: unset;
  width: 100%;
  height: 100%;
  border-radius: var(--border-radius-small);
  position: relative;
  background-color: ${({ $isUnassigned, $isEmpty, $isRequired }) => {
    if ($isUnassigned) return READ_ONLY_BG;
    if ($isRequired) {
      if ($isEmpty) return EMPTY_REQUIRED_BG;
      return FILLED_BG;
    } else {
      if ($isEmpty) return EMPTY_OPTIONAL_BG;
      return FILLED_BG;
    }
  }};
  ${bodyFontSizeStyles}
  overflow: hidden;
  cursor: ${({ $isUnassigned }) => ($isUnassigned ? 'not-allowed' : 'pointer')};

  &:hover {
    background-color: ${({ $isUnassigned, $isEmpty }) => {
      if ($isUnassigned) return READ_ONLY_HOVER_BG;
      if ($isEmpty) return EMPTY_HOVER_BG;
      return FILLED_HOVER_BG;
    }};
  }

  &:focus,
  &:focus-within,
  &:focus-visible {
    ${focusVisibleStylesNoWrapper}
  }
`;

// ------------------------------------------------------------------------------------
// Begin checkbox styles
// ------------------------------------------------------------------------------------

// Note: we're rendering a checkbox for both radio and checkbox inputs, so we're rendering
// a hidden input element and using a styled `div` as the visual checkbox instead of trying
// to get consistent styling across checkbox and radio inputs.

// TODO(tokens): Replace hex color value #F4E4D799 with numbered equivalent once available.
const CHECKBOX_EMPTY_BG = '#F4E4D799';
// TODO(tokens): Replace hex color value #EFEDF1CC with numbered equivalent once available.
const CHECKBOX_READ_ONLY_EMPTY_BG = '#EFEDF1CC';
// TODO(tokens): Replace hex color value #2A1A3E80 with numbered equivalent once available.
const CHECKBOX_READ_ONLY_CHECKED_BG = '#2A1A3E80';

export const StyledCheckbox = styled.div.attrs({
  // @ts-expect-error Adding a custom attribute to allow this to receive focus instead of the actual checkbox
  'data-focusable': 'true',
})<{
  $checked: boolean;
  $isUnassigned?: boolean;
}>`
  margin-right: auto;
  margin-bottom: auto;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border-style: solid;
  border-width: 1px;
  aspect-ratio: 1;
  background-color: ${({ $checked, $isUnassigned }) => {
    if ($isUnassigned) {
      if ($checked) return CHECKBOX_READ_ONLY_CHECKED_BG;
      return CHECKBOX_READ_ONLY_EMPTY_BG;
    }
    if ($checked) return 'var(--purple-900)';
    return CHECKBOX_EMPTY_BG;
  }};
  // The checkmark will render in this color
  color: var(--white);
  border-radius: var(--border-radius-small);
  border-color: ${({ $isUnassigned, $checked }) => {
    if (!$isUnassigned && !$checked) return 'var(--grayscale-900)';
    return 'transparent';
  }};

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $isUnassigned }) => ($isUnassigned ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;

  &:hover,
  &.hover {
    border-width: 2px;
    border-color: var(--grayscale-900);
  }

  &:focus,
  &:focus-within,
  &.focus,
  &.focus-within {
    outline: none;
    ${focusVisibleStylesNoWrapper}
  }
`;

// Page overlay for form field overlays on PDF pages
export const PageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 50;
`;
